import React, { useMemo } from 'react';
import type { SelectedDesignForPricing } from '../../../../../store/oneButtonStore';
import { formatLayoutNotSellableReason, isLayoutProductionOverAnnualUsage, resolveLayoutRoofCount, resolveSiteTreeCount } from '../utils/layout';
import { DashboardAiCatalogListRow, type DashboardAiCatalogListRowStatusVariant } from './DashboardAiCatalogListRow';
import {
    resolveCategoryCardDisplayLabel,
    type CategoryLayoutCandidate,
} from '../utils/categoryBestVariantScoring';
import {
    DashboardAiDesignsSourceBadge,
    type AiDesignsDataSource,
} from './DashboardAiDesignsSourceBadge';
import { hasSimulatedLayoutCatalogData } from '../utils/aiLayoutCacheHydration';
import { resolveCatalogCardSnapshot, resolveDefaultFallbackSnapshotUrl, catalogCardSnapshotForDisplay } from '../utils/catalogCardSnapshot';
import { useViewerStore } from '../../../../../store/store';
import { STABLE_EMPTY_OBJECT } from '../utils/stableEmpty';

export type DashboardAiSlideDesignsListProps = {
    theme: 'dark' | 'light';
    layoutCategoryNames: string[];
    layoutCandidatesByCategory: Record<string, unknown>;
    layoutProductionByKey: Record<string, number | undefined>;
    layoutModulesByKey: Record<string, number | undefined>;
    defaultMaxfillRoofIds: string[] | undefined;
    roofCards: any[];
    roofCombinationsFromAi: any[];
    layoutMetricsByKey: Record<string, unknown>;
    selectedDesignForPricing: SelectedDesignForPricing | null;
    /** Row currently receiving a financing fetch (when $ was used without changing panel selection). */
    pricingDesignForTrigger: SelectedDesignForPricing | null;
    simulatingLayouts: boolean;
    activeModule: unknown;
    annualUsageCapKwh: number | null | undefined;
    treeCount: number;
    layoutSnapshotsByKey: Record<string, string | undefined>;
    aiCategoryLayoutVariants: Record<string, unknown[]>;
    isLoadingSnapshots: boolean;
    handleSelectLayoutCard: (key: string, details: any) => void;
    handleApplyLayoutClick: (key: string, categoryLabel?: string) => void;
    pricingSelectionLoading: boolean;
    handlePricingDollarClick: (key: string, details: {
        estProd: number | undefined;
        estModules: number | undefined;
        systemKw: string;
        /** Preferred: raw system size in kW (full precision, not display-rounded). */
        systemSizeKw?: number;
        roofCount: number;
        categoryId: string;
        designId: string;
    }) => void;
    /** Re-run full AI layout generation (same as dashboard Run AI). */
    onReRunAiDesigns: () => void;
    reRunAiLoading: boolean;
    reRunAiDisabled: boolean;
    selectedSavedDesignLabel?: string | null;
    aiDesignsDataSource?: AiDesignsDataSource;
};

type LayoutMetricSlice = {
    roofCount?: number | null;
};

/**
 * AI layout list for the slide rail — retail-style catalog rows (index, thumb, name, summary, status, kW, actions).
 */
export function DashboardAiSlideDesignsList(props: DashboardAiSlideDesignsListProps) {
    const {
        theme,
        layoutCategoryNames,
        layoutCandidatesByCategory,
        layoutProductionByKey,
        layoutModulesByKey,
        defaultMaxfillRoofIds,
        roofCards,
        roofCombinationsFromAi,
        layoutMetricsByKey,
        selectedDesignForPricing,
        pricingDesignForTrigger,
        simulatingLayouts,
        activeModule,
        annualUsageCapKwh,
        treeCount,
        layoutSnapshotsByKey,
        aiCategoryLayoutVariants,
        isLoadingSnapshots,
        handleSelectLayoutCard,
        handleApplyLayoutClick,
        pricingSelectionLoading,
        handlePricingDollarClick,
        onReRunAiDesigns,
        reRunAiLoading,
        reRunAiDisabled,
        selectedSavedDesignLabel,
        aiDesignsDataSource = null,
    } = props;

    const categoryMainLayoutByCategory = useViewerStore(
        (s) =>
            (s.categoryMainLayoutByCategory ?? STABLE_EMPTY_OBJECT) as Record<string, CategoryLayoutCandidate>,
    );
    const aiLayoutVariantSnapshots = useViewerStore(
        (s: any) => (s.aiLayoutVariantSnapshots ?? STABLE_EMPTY_OBJECT) as Record<string, string>,
    );
    const layoutRoofSummariesByKey = useViewerStore(
        (s) => (s.layoutRoofSummariesByKey ?? STABLE_EMPTY_OBJECT) as Record<string, unknown[]>,
    );

    const modulePowerW = (activeModule as any)?.power ?? 0;

    const rows = useMemo(() => {
        const opts = [
            { value: 'default', baseLabel: 'oneDRAW Default Maxfill' },
            ...layoutCategoryNames
                .map((cat) => {
                    const first = layoutCandidatesByCategory[cat]?.[0] as { key: string } | undefined;
                    if (!first?.key) return null;
                    return { value: first.key, baseLabel: cat };
                })
                .filter((o): o is { value: string; baseLabel: string } => o != null),
        ];

        return opts.map((opt) => {
            const key = opt.value;
            const cardKey = `${opt.baseLabel}-${key}`;
            const estProdRaw = layoutProductionByKey[key];
            const estModulesRaw = layoutModulesByKey[key];
            const roofCountRaw = resolveLayoutRoofCount({
                layoutKey: key,
                layoutMetricsByKey: layoutMetricsByKey as Record<string, LayoutMetricSlice>,
                defaultMaxfillRoofIds,
                roofCards,
                roofCombinationsFromAi,
                categoryMainLayoutByCategory,
                layoutRoofSummariesByKey,
            });
            const isSimulating = simulatingLayouts && estProdRaw === undefined && estModulesRaw === undefined;
            const systemSizeKwValue =
                estModulesRaw != null && Number.isFinite(estModulesRaw) && modulePowerW > 0
                    ? (Number(estModulesRaw) * Number(modulePowerW)) / 1000
                    : undefined;
            const systemKw =
                systemSizeKwValue != null && Number.isFinite(systemSizeKwValue) && systemSizeKwValue > 0
                    ? systemSizeKwValue.toFixed(2)
                    : '—';
            const treesLabel = String(resolveSiteTreeCount(treeCount));
            const prodDisplay =
                estProdRaw !== undefined && Number.isFinite(estProdRaw)
                    ? `${Math.round(estProdRaw).toLocaleString()} kWh`
                    : '—';
            const disqualifiedOverUsage =
                !isSimulating && isLayoutProductionOverAnnualUsage(estProdRaw, annualUsageCapKwh);
            const isSelected = selectedDesignForPricing?.designId === cardKey;
            const displayLabel = resolveCategoryCardDisplayLabel(
                opt.baseLabel,
                categoryMainLayoutByCategory,
                layoutCandidatesByCategory[opt.baseLabel]?.[0] as CategoryLayoutCandidate | undefined,
            );

            return {
                cardKey,
                key,
                baseLabel: opt.baseLabel,
                label: displayLabel,
                prodDisplay,
                systemKw,
                systemSizeKw: systemSizeKwValue,
                modules: estModulesRaw !== undefined ? String(estModulesRaw) : '—',
                roofs: roofCountRaw != null ? String(roofCountRaw) : '—',
                trees: treesLabel,
                isSimulating,
                disqualifiedOverUsage,
                isSelected,
                estProd: estProdRaw,
                estModules: estModulesRaw,
                roofCount: roofCountRaw,
            };
        });
    }, [
        layoutCategoryNames,
        layoutCandidatesByCategory,
        layoutProductionByKey,
        layoutModulesByKey,
        defaultMaxfillRoofIds,
        roofCards,
        roofCombinationsFromAi,
        layoutMetricsByKey,
        categoryMainLayoutByCategory,
        layoutRoofSummariesByKey,
        treeCount,
        simulatingLayouts,
        modulePowerW,
        annualUsageCapKwh,
        selectedDesignForPricing?.designId,
        categoryMainLayoutByCategory,
        layoutCandidatesByCategory,
    ]);

    const fallbackSnapshotUrl = useMemo(
        () => resolveDefaultFallbackSnapshotUrl(layoutSnapshotsByKey, aiDesignsDataSource),
        [layoutSnapshotsByKey, aiDesignsDataSource],
    );

    const hasOnlyDefaultPlaceholder =
        !hasSimulatedLayoutCatalogData(layoutProductionByKey, layoutModulesByKey) &&
        rows.length === 1 &&
        rows[0]?.key === 'default' &&
        rows[0]?.prodDisplay === '—' &&
        rows[0]?.modules === '—' &&
        rows[0]?.systemKw === '—';

    const savedDesignMessage = selectedSavedDesignLabel
        ? `This is a saved design (${selectedSavedDesignLabel}). Run AI Design to generate simulated layout cards and stats.`
        : 'Run AI Design to generate simulated layout cards and stats.';

    return (
        <div className={`obd-ai-catalog-list obd-ai-catalog-list--slide-rail obd-theme-${theme}`} role="list">
            <div className="obd-ai-catalog-list__headrow">
                <div className="obd-ai-catalog-list__headrow-left">
                    <div className="obd-ai-catalog-list__subhead">AI layouts</div>
                    <DashboardAiDesignsSourceBadge source={aiDesignsDataSource} theme={theme} />
                    {isLoadingSnapshots && aiDesignsDataSource === 'db' ? (
                        <span
                            className="obd-ai-designs-snapshot-loading-hint"
                            role="status"
                            aria-live="polite"
                        >
                            Loading snapshots from DB…
                        </span>
                    ) : null}
                </div>
                <button
                    type="button"
                    className="obd-ai-catalog-list__rerun"
                    onClick={() => onReRunAiDesigns()}
                    disabled={reRunAiDisabled}
                    aria-busy={reRunAiLoading}
                >
                    {reRunAiLoading ? 'Running…' : hasOnlyDefaultPlaceholder ? 'Run AI Design' : 'Re-run AI Designs'}
                </button>
            </div>
            {hasOnlyDefaultPlaceholder ? (
                <div
                    style={{
                        padding: '10px 2px 2px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        alignItems: 'flex-start',
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            fontSize: 13,
                            lineHeight: 1.45,
                            color: theme === 'dark' ? 'rgba(156,163,175,0.95)' : 'rgba(71,85,105,0.95)',
                        }}
                    >
                        {savedDesignMessage}
                    </p>
                </div>
            ) : (
            rows.map((r, rowIndex) => {
                const pricingBusy =
                    pricingSelectionLoading &&
                    (selectedDesignForPricing?.designId === r.cardKey ||
                        pricingDesignForTrigger?.designId === r.cardKey);
                const summaryLine = `${r.modules} modules · ${r.roofs} roofs · ${r.trees} trees`;
                const statusLabel = r.isSimulating
                    ? 'Calculating'
                    : r.disqualifiedOverUsage
                      ? 'Below usage'
                      : r.isSelected
                        ? 'Active'
                        : 'Ready';
                let statusVariant: DashboardAiCatalogListRowStatusVariant = 'neutral';
                if (r.isSimulating) statusVariant = 'amber';
                else if (r.disqualifiedOverUsage) statusVariant = 'muted';
                else if (r.isSelected) statusVariant = 'lime';

                const selectPayload = {
                    estProd: r.estProd,
                    estModules: r.estModules,
                    systemKw: r.systemKw,
                    systemSizeKw: (r as any).systemSizeKw,
                    roofCount: r.roofCount,
                    categoryId: r.baseLabel,
                    designId: r.cardKey,
                };

                const thumbSnapshotResult = catalogCardSnapshotForDisplay(
                    resolveCatalogCardSnapshot({
                        layoutKey: r.key,
                        categoryLabel: r.label,
                        layoutSnapshotsByKey,
                        categoryMainLayoutByCategory,
                        aiCategoryLayoutVariants,
                        aiLayoutVariantSnapshots,
                        defaultFallbackSnapshotUrl: fallbackSnapshotUrl,
                        aiDesignsDataSource,
                        layoutHasMetrics:
                            (r.estModules != null && Number.isFinite(r.estModules) && r.estModules > 0) ||
                            (r.estProd != null && Number.isFinite(r.estProd) && r.estProd > 0),
                    }),
                    isLoadingSnapshots,
                );

                return (
                    <DashboardAiCatalogListRow
                        key={r.cardKey}
                        theme={theme}
                        rowIndex={rowIndex}
                        cardKey={r.cardKey}
                        showThumbIndex={false}
                        snapshotUrl={thumbSnapshotResult.url}
                        snapshotError={thumbSnapshotResult.error}
                        snapshotLoading={isLoadingSnapshots && !thumbSnapshotResult.url}
                        showNotSellableStamp={r.disqualifiedOverUsage}
                        notSellableReason={
                            r.disqualifiedOverUsage
                                ? formatLayoutNotSellableReason(annualUsageCapKwh)
                                : undefined
                        }
                        label={r.label}
                        summaryLine={summaryLine}
                        prodLine={r.prodDisplay}
                        statusLabel={statusLabel}
                        statusVariant={statusVariant}
                        systemKw={r.systemKw}
                        isSelected={r.isSelected}
                        isSimulating={r.isSimulating}
                        disqualified={r.disqualifiedOverUsage}
                        selectForPricingDisabled={
                            r.disqualifiedOverUsage ||
                            (pricingSelectionLoading &&
                                (r.isSelected || pricingDesignForTrigger?.designId === r.cardKey))
                        }
                        pricingBusy={pricingBusy}
                        pricingDisabled={r.disqualifiedOverUsage || pricingBusy}
                        onSelectForPricing={() => handleSelectLayoutCard(r.key, selectPayload)}
                        onActivateDesign={() => {
                            if (r.disqualifiedOverUsage || r.isSimulating) return;
                            if (!r.isSelected) {
                                handleSelectLayoutCard(r.key, selectPayload);
                            }
                            void handleApplyLayoutClick(
                                r.baseLabel === 'oneDRAW Default Maxfill' ? 'default' : r.key,
                                r.baseLabel === 'oneDRAW Default Maxfill' ? undefined : r.baseLabel,
                            );
                        }}
                        onPricing={() =>
                            handlePricingDollarClick(r.key, {
                                estProd: r.estProd,
                                estModules: r.estModules,
                                systemKw: r.systemKw,
                                systemSizeKw: (r as any).systemSizeKw,
                                roofCount:
                                    typeof r.roofCount === 'number' && Number.isFinite(r.roofCount)
                                        ? r.roofCount
                                        : 0,
                                categoryId: r.baseLabel,
                                designId: r.cardKey,
                            })
                        }
                        onOpen3D={() => {
                            void handleApplyLayoutClick(
                                r.baseLabel === 'oneDRAW Default Maxfill' ? 'default' : r.key,
                                r.baseLabel === 'oneDRAW Default Maxfill' ? undefined : r.baseLabel,
                            );
                        }}
                    />
                );
            })
            )}
        </div>
    );
}
